import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, RagDot } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Sparkles, Send } from "lucide-react";

function currentPeriod() {
  const d = new Date();
  const onejan = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d - onejan) / 86400000 + onejan.getDay() + 1) / 7);
  return `${d.getFullYear()}-W${String(week).padStart(2, "0")}`;
}

export default function TeamStatusPage() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const [period, setPeriod] = useState(currentPeriod());
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState({ rag: "Green", progress: "", planned: "", blockers: "" });
  const [busy, setBusy] = useState(false);
  const [consolidating, setConsolidating] = useState(false);

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/artifacts/personal_status_reports`).then((r) => {
      setReports(r.data.sort((a, b) => (b.period || "").localeCompare(a.period || "")));
    }).catch((e) => toast.error(errMsg(e)));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const mine = reports.find((r) => r.userId === user?.id && r.period === period);
    setForm(mine ? { rag: mine.rag || "Green", progress: mine.progress || "", planned: mine.planned || "", blockers: mine.blockers || "" }
      : { rag: "Green", progress: "", planned: "", blockers: "" });
  }, [reports, period, user]);

  const submit = async () => {
    setBusy(true);
    const mine = reports.find((r) => r.userId === user?.id && r.period === period);
    const payload = { ...form, period, userId: user.id, userName: user.name };
    try {
      if (mine) await api.put(`/projects/${projectId}/artifacts/personal_status_reports/${mine.id}`, { ...mine, ...payload });
      else await api.post(`/projects/${projectId}/artifacts/personal_status_reports`, payload);
      toast.success(`Status update submitted for ${period}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const consolidate = async () => {
    setConsolidating(true);
    const periodReports = reports.filter((r) => r.period === period);
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/generate`, {
        generationType: "statusreport",
        context: { personalStatusReports: periodReports.map((r) => ({ member: r.userName, rag: r.rag, progress: r.progress, planned: r.planned, blockers: r.blockers })), reportingPeriod: period },
      });
      await api.post(`/projects/${projectId}/artifacts/status_reports`, { ...data.result, reportingPeriod: `Consolidated — ${period}` });
      toast.success("Consolidated project status report saved — see Status Reports");
    } catch (e) { toast.error(errMsg(e)); }
    setConsolidating(false);
  };

  const periodReports = reports.filter((r) => r.period === period);
  const periods = [...new Set([currentPeriod(), ...reports.map((r) => r.period).filter(Boolean)])];

  return (
    <div data-testid="team-status-page">
      <PageHeader overline="Reporting · Per-person visibility" title="Team Status Updates"
        actions={
          <Button data-testid="consolidate-status-btn" className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]"
            onClick={consolidate} disabled={consolidating || periodReports.length === 0}>
            <Sparkles className="h-4 w-4 mr-1" />{consolidating ? "Consolidating…" : "Consolidate for PM / Sponsor"}
          </Button>
        }>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-muted-foreground">Period</span>
          <select data-testid="period-select" className="border border-input px-2 py-1 text-sm bg-background" value={period} onChange={(e) => setPeriod(e.target.value)}>
            {periods.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <Input className="w-32 h-8 text-sm" placeholder="e.g. 2026-W26" onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) setPeriod(e.target.value); }} />
        </div>
      </PageHeader>

      <div className="grid lg:grid-cols-[380px_1fr] gap-4">
        <div className="border border-border bg-card p-4 h-fit" data-testid="my-status-form">
          <div className="overline-label mb-2">My update — {user?.name} · {period}</div>
          <Label className="text-xs">My RAG</Label>
          <div className="flex gap-2 mb-2 mt-1">
            {["Green", "Amber", "Red"].map((r) => (
              <button key={r} data-testid={`rag-${r}`} onClick={() => setForm({ ...form, rag: r })}
                className={`px-2 py-1 text-xs border ${form.rag === r ? "border-black font-bold" : "border-border"}`}>
                <RagDot value={r} />
              </button>
            ))}
          </div>
          <Label className="text-xs">Progress this period</Label>
          <Textarea data-testid="status-progress-input" rows={3} value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} />
          <Label className="text-xs mt-2 block">Planned next</Label>
          <Textarea data-testid="status-planned-input" rows={2} value={form.planned} onChange={(e) => setForm({ ...form, planned: e.target.value })} />
          <Label className="text-xs mt-2 block">Blockers / support needed</Label>
          <Textarea data-testid="status-blockers-input" rows={2} value={form.blockers} onChange={(e) => setForm({ ...form, blockers: e.target.value })} />
          <Button data-testid="submit-status-btn" className="rounded-none w-full mt-3" onClick={submit} disabled={busy || !form.progress}>
            <Send className="h-4 w-4 mr-1" />{busy ? "Saving…" : "Submit my update"}
          </Button>
        </div>

        <div className="space-y-3">
          <div className="overline-label">Team submissions — {period} ({periodReports.length})</div>
          {periodReports.length === 0 && <div className="border border-dashed border-border p-8 text-center text-muted-foreground text-sm">No updates for this period yet.</div>}
          {periodReports.map((r) => (
            <div key={r.id} data-testid="personal-report-card" className="border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="font-display font-bold">{r.userName}</span>
                <RagDot value={r.rag} />
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mt-2 text-sm">
                <div><div className="overline-label">Progress</div><p className="mt-0.5">{r.progress || "—"}</p></div>
                <div><div className="overline-label">Planned</div><p className="mt-0.5">{r.planned || "—"}</p></div>
                <div><div className="overline-label">Blockers</div><p className="mt-0.5">{r.blockers || "—"}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
